import { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, useNavigate } from 'react-router-dom';
import { Hero } from './components/Hero';
import { TheMirror } from './components/TheMirror';
import { Projects } from './components/Projects';
import { Experience } from './components/Experience';
import { Navigation } from './components/Navigation';
import { Footer } from './components/Footer';
import { Blog } from './pages/Blog';
import { Testimonials } from './components/Testimonials';
import { Contact } from './pages/Contact';
import { Terms } from './pages/Terms';
import { Cookies } from './pages/Cookies';
import { Privacy } from './pages/Privacy';
import { AboutIntro } from './components/AboutIntro';
import { Solutions } from './pages/Solutions';
import { Vector } from './pages/Vector';
import AIChat from './components/AIChat';
import { CookieConsent } from './components/CookieConsent';
import { AnalyticsScripts } from './components/AnalyticsScripts';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import SEO from './components/SEO';
import { Toaster } from 'react-hot-toast';
import { trackPageView } from './lib/analytics';
import './i18n/config';
import { AuthGate } from './components/AuthGate';
import CRMLayout from './components/CRMLayout';
import { Login } from './pages/Login';
import CRMRouter from './pages/crm/CRMRouter';
import { ToolShowcase } from './components/ToolShowcase';
import ResetPassword from './pages/ResetPassword';
import DemoLogin from './pages/DemoLogin';
import { ThemeProvider } from './context/ThemeContext';
const queryClient = new QueryClient();

// Route change tracking component
function RouteChangeTracker() {
  const location = useLocation();
  
  useEffect(() => {
    // Track page view when location changes
    trackPageView(location.pathname);
  }, [location]);
  
  return null;
}

function RecoveryRedirect() {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const hash = window.location.hash;
    if (hash && /type=(recovery|invite|signup)/.test(hash) && location.pathname !== '/reset-password') {
      navigate(`/reset-password${hash}`);
    }
  }, [location, navigate]);

  return null;
}

function HomePage() {
  return (
    <>
      <SEO
        title="Victor Lazari – Founder & Chief Architect"
        description="Victor Lazari, Founder & Architetto di Varienda-BMS. Progetto sistemi AI e automazione che trasformano il caos operativo in vantaggio competitivo. Soluzioni ad alto impatto per il controllo e la crescita."
        canonical="https://victorlazari.com/"
        image="https://raw.githubusercontent.com/VictorLazari-lab/victorlazari.com/VictorLazari-Media/varienda%20minimalist%20logo.png"
        type="website"
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'Person',
          name: 'Victor Lazari',
          url: 'https://victorlazari.com/',
          jobTitle: 'Founder & Chief Architect'
        }}
      />
      <Hero />
      <TheMirror />
      <Projects />
      <Experience />
    </>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <Router>
          <AppContent />
        </Router>
      </ThemeProvider>
    </QueryClientProvider>
  );
}

function AppContent() {
  const location = useLocation();
  const isInCRM = location.pathname.startsWith('/crm');

  // Prevent body scroll when in CRM
  useEffect(() => {
    if (isInCRM) {
      const originalOverflow = window.getComputedStyle(document.body).overflow;
      const originalPosition = window.getComputedStyle(document.body).position;
      const originalHeight = window.getComputedStyle(document.body).height;
      
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
      document.body.style.height = '100%';
      
      return () => {
        document.body.style.overflow = originalOverflow;
        document.body.style.position = originalPosition;
        document.body.style.width = '';
        document.body.style.height = originalHeight;
      };
    }
  }, [isInCRM]);

  return (
    <div className="relative min-h-screen bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 flex flex-col">
      <div className="relative z-10 flex-1">
        <RouteChangeTracker />
        <RecoveryRedirect />
        {!isInCRM && <Navigation />}
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/login" element={<Login />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/blog" element={<Blog />} />
          <Route path="/blog/:slug" element={<Blog />} />
          <Route path="/testimonials" element={<Testimonials />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/solutions" element={<Solutions />} />
          <Route path="/demo-login" element={<DemoLogin />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/cookies" element={<Cookies />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/about" element={
            <>
              <AboutIntro />
              <ToolShowcase />
            </>
          } />
          <Route path="/vector" element={<Vector />} />
          <Route path="/crm/*" element={
            <AuthGate>
              <CRMLayout>
                <CRMRouter />
              </CRMLayout>
            </AuthGate>
          } />
        </Routes>
      </div>
      {!isInCRM && (
        <div className="relative z-10">
          <Footer />
        </div>
      )}
      <AIChat />
      <CookieConsent />
      <AnalyticsScripts />
      <Toaster 
        position="top-right"
        toastOptions={{
          style: {
            background: 'var(--toast-bg, #ffffff)',
            color: 'var(--toast-color, #111827)',
            border: 'var(--toast-border, 1px solid #e5e7eb)',
          },
          className: 'dark:bg-gray-800 dark:text-gray-100 dark:border-gray-700',
        }}
      />
    </div>
  );
}

export default App;